// @ts-nocheck
/**
 * IVR-nodes-scoped react-bootstrap compat shim.
 *
 * The 10 IVR node components (Answer/Api/Conditional/Default/Dtmf/Hangup/
 * Language/Missed/Play/Record/Transfer/Voicemail) all import the same tiny
 * surface â€” `Button`, `Form.*` (Group/Label/Control/Select/Check/Text), and
 * (Conditional-only) `Table`.
 *
 * Rather than duplicate ~80 lines of inline micro-shim across every node
 * file OR keep the global `@/lib/rb-shim` dependency alive here, this
 * module re-exports the same compound API scoped to the IVR nodes tree.
 * Buttons come straight from shadcn; the Form.* helpers render native
 * `form-control` / `form-check` markup so the existing `.ivr-*` node CSS
 * keeps driving the visual style.
 *
 * When it's worth converting these node files to shadcn Input/Select/etc.
 * directly, replace the imports in each node file â€” this shim stays intact
 * for the rest until they're all migrated.
 */
import * as React from 'react';
import { Button as ShadcnButton } from '@/components/ui/button';

const cx = (...args: any[]) => args.filter(Boolean).join(' ');

/* â”€â”€ Button â€” shadcn primary; keeps `variant` mapping for react-bootstrap
 *   variant names used inside the IVR node forms (primary/secondary/
 *   danger/outline-*). */
const variantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  success: 'default',
  info: 'default',
  warning: 'default',
  light: 'outline',
  dark: 'default',
  link: 'link',
  'outline-primary': 'outline',
  'outline-secondary': 'outline',
  'outline-danger': 'outline',
  'outline-success': 'outline',
  'outline-info': 'outline',
  'outline-warning': 'outline',
};

export const Button = ({ variant = 'primary', size, className, children, ...props }: any) => (
  <ShadcnButton
    variant={variantMap[variant] || 'default'}
    size={size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : undefined}
    className={className}
    {...props}
  >
    {children}
  </ShadcnButton>
);

/* â”€â”€ Form (compound) â€” native form-control markup */
const _Form: any = ({ className, children, onSubmit, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
_Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={cx('mb-3', className)} {...props}>
    {children}
  </div>
);
_Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
_Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
_Form.Select = ({ className, children, ...props }: any) => (
  <select className={cx('form-control form-select', className)} {...props}>
    {children}
  </select>
);
_Form.Check = ({ type = 'checkbox', label, id, className, inline, children, ...props }: any) => (
  <div className={cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
_Form.Text = ({ muted, className, children, ...props }: any) => (
  <small className={cx('form-text', muted && 'text-muted', className)} {...props}>
    {children}
  </small>
);
_Form.Control.Feedback = ({ type: _t, className, children, ...props }: any) => (
  <div className={cx('text-xs text-destructive', className)} {...props}>
    {children}
  </div>
);
export const Form = _Form;

/* â”€â”€ Table â€” native `<table>` with bootstrap `.table.*` classes */
export const Table = ({
  className,
  striped: _striped,
  bordered,
  borderless: _borderless,
  hover: _hover,
  size,
  responsive,
  children,
  ...props
}: any) => {
  const table = (
    <table
      className={cx(
        'w-full text-sm',
        bordered && 'border border-border',
        size === 'sm' ? 'text-xs' : '',
        className,
      )}
      {...props}
    >
      {children}
    </table>
  );
  return responsive ? <div className="overflow-x-auto">{table}</div> : table;
};
